import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { MentoringFlagsService } from '@app/mentoring/services/mentoring-flags.service';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { EngagedLearnerService } from '@app/shared/services/engaged-learner.service';
import { TagsService } from '@app/tags/services/tags.service';
import { UserInterest, UserProfileSummary } from '@app/user/user-api';
import { TranslateService } from '@ngx-translate/core';
import { Profile } from '@app/profile/profile';
import { ProfileService } from '@app/profile/services/profile.service';
import { AuthService } from '@app/shared/services/auth.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { ModalService } from '@dg/shared-services';
import { FollowingCount } from '@app/profile/profile-api';
import { UserInfoBioModal } from '@app/profile/components/profile-header/user-info/user-info-bio-modal.component';
import { Router } from '@angular/router';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { ProfileSocialModal } from '@app/profile/components/profile-header/profile-social-modal/profile-social-modal.component';
import {
  ProfileSocialModalFacade,
  SocialModalVM,
} from '@app/profile/components/profile-header/profile-social-modal/profile-social-modal.facade';

export interface UserInfoOptions {
  isEngaged?: boolean;
  isRestrictedProfile?: boolean;
  ownerIsViewing?: boolean;
  viewerIsBlocked?: boolean;
  isSkillInventoryProfile?: boolean;
  isSkillAnalyticsProfile?: boolean;
  settingsUrl?: string;
  privacyId?: number;
}

@Component({
  selector: 'dgx-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfo extends SubscriberBaseDirective implements OnInit {
  @Input() public profile: Profile;
  @Input() public options: UserInfoOptions = {};
  @ViewChild('connectWith') public connectWithRef: ElementRef<HTMLElement>;

  public socialModalVM$: Observable<SocialModalVM>;

  public i18n = this.translate.instant([
    'Core_ActiveLearner',
    'Core_MoreOptions',
    'UserInfoPartial_EditProfile',
    'Opportunities_ConnectWithModal_OpenModal',
    'Core_ShowMore',
    'Core_JobRoleLiteral',
    'User_BioLabel',
    'Core_Location',
    'Core_MoreOptions',
    'Opportunities_ConnectWithButton_Status_Accepted',
    'Core_Add',
    'Core_Followers',
    'Core_Following',
    'Core_Groups',
    'Core_AboutMe',
    'Core_AddBio',
  ]);
  public metaLabelTextLimit = 35;
  public metaLabelTooltipTextLimit = 100;

  public isManagedProfile: boolean;
  public groupsLabel: string;
  public followersLabel: string;
  public followingLabel: string;

  public displayShowMore = false;
  public nonOwnerMenuConfig: MenuViewModel[] = [];
  public ownerMenuConfig: MenuViewModel[] = [];
  public bio = '';
  public showEngagedLearnerBadge: boolean;
  public showMentorOptions = false;
  public counts: FollowingCount = {
    followees: 0,
    followers: 0,
  };

  public hideCounts = false;
  public isMobile = false;

  private wordLimit = 150; // this is enforced in settings now, but older profiles may be longer than 150 words

  public get ownerIsViewing() {
    return this.profileService.ownerIsViewing;
  }

  public get isAuthenticated() {
    return this.authService.isLoggedIn;
  }

  public get showMenu() {
    return this.isAuthenticated;
  }

  public get menuConfig() {
    return this.ownerIsViewing ? this.ownerMenuConfig : this.nonOwnerMenuConfig;
  }

  /**
   * Fix Linter Issue
   * Gets "Profile" as "UserProfileSummary" for use in with dgx-connect-with-mentor-button
   * TODO: Refactor of types to make this cast unnecessary
   */
  public get mentor(): UserProfileSummary {
    return this.profile as unknown as UserProfileSummary;
  }

  constructor(
    private mentoringFlagsService: MentoringFlagsService,
    private tagsService: TagsService,
    private translate: TranslateService,
    private engagedService: EngagedLearnerService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private profileService: ProfileService,
    private authService: AuthService,
    private modalService: ModalService,
    private router: Router,
    private windowLayoutService: WindowLayoutService,
    private profileSocialModalFacade: ProfileSocialModalFacade,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.isManagedProfile = this.authService?.authUser?.isManagedProfile;
    this.socialModalVM$ = this.profileSocialModalFacade.vm$;

    this.isMobile = this.windowLayoutService.isMobile;
    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
        this.cdr.detectChanges();
      });

    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });

    this.hideCounts =
      this.options.isSkillInventoryProfile ||
      this.options.isSkillAnalyticsProfile ||
      !this.isAuthenticated;

    this.showEngagedLearnerBadge =
      this.engagedService.isEnabled && this.profile.isEngaged;
    this.bio = (this.profile.bio || '')
      .split(' ')
      .slice(0, this.wordLimit)
      .join(' ');

    this.nonOwnerMenuConfig =
      this.profileOverviewActionsService.getNonOwnerUserInfoMenu(this.profile);
    this.ownerMenuConfig =
      this.profileOverviewActionsService.getOwnerUserInfoMenu(
        this.profile,
        this.isMobile
      );
    this.showMentorOptions =
      !this.ownerIsViewing &&
      this.authService.authUser?.hasCareerMobility &&
      this.profile.isMentoring &&
      this.mentoringFlagsService.showMentorshipOptions;

    this.profileSocialModalFacade.initCounts(this.profile.userProfileKey);

    // don't try to load if userProfileKey isn't present - eg when a page is viewed by a logged out user
    // GetPersonalSettings!
    if (this.showMentorOptions && this.profile.userProfileKey) {
      // TODO: Remove all of this, once we're getting `mentoringTags` back
      // from the BE.
      if (!this.profile.mentoringTags?.length) {
        this.tagsService
          .getUserProfileTags(this.profile.userProfileKey)
          .pipe(this.takeUntilDestroyed())
          .subscribe(
            ({
              focus,
              required,
              other,
            }: {
              focus: UserInterest[];
              required: UserInterest[];
              other: UserInterest[];
            }) => {
              this.profile.mentoringTags = [...focus, ...required, ...other]
                .filter((tag) => !!tag.isMentoring)
                .map((tag) => tag.name);
            }
          );
      }
    }
  }

  public showEngagedLearnerModal(): void {
    this.engagedService.showEngagedLearnerDetails({
      ...this.profile,
      picture: this.profile.pic,
      isEngaged: this.profile.isEngaged,
    });
  }

  public onBioClamped(isClamped): void {
    this.displayShowMore = isClamped;
  }

  public showBioModal(): void {
    const inputs = {
      inputs: {
        bio: this.bio,
        profile: this.profile,
        options: this.options,
        isActiveLearner: this.showEngagedLearnerBadge,
      },
    };
    this.modalService.show(UserInfoBioModal, inputs).pipe(take(1)).subscribe();
  }

  public goToSettings(): void {
    this.router.navigateByUrl(`/profile/${this.profile.vanityUrl}/settings`, {
      replaceUrl: true,
    });
  }

  public showSocialModal(defaultTab: string) {
    this.modalService
      .show(ProfileSocialModal, {
        inputs: {
          defaultTab,
          profile: this.profile,
          ownerIsViewing: this.ownerIsViewing,
        },
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }
}
